import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  Fab,
} from '@mui/material';

import {
  AddOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PersonOutlined,
} from '@mui/icons-material';
import { useClientStore } from '../stores/useClientStore';
import { Client } from '../types';
import { useNotification } from '../contexts/NotificationContext';

const ClientsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { clients, loadClients, deleteClient, isLoading } = useClientStore();
  const { showConfirm, showSuccess } = useNotification();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const filteredClients = clients.filter((client) => {
    const search = searchQuery.toLowerCase();
    return (
      client.nom.toLowerCase().includes(search) ||
      client.prenom.toLowerCase().includes(search) ||
      client.email.toLowerCase().includes(search) ||
      client.telephone.includes(search)
    );
  });

  const handleDelete = async (id: number) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le client',
      message: 'Êtes-vous sûr de vouloir supprimer ce client ? Tous les devis et factures associés seront également supprimés.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error',
    });

    if (confirmed) {
      try {
        await deleteClient(id);
        showSuccess('Client supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression du client', error);
      }
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 700, color: '#333', mb: 3 }}
      >
        Clients
      </Typography>

      {/* Barre de recherche */}
      <TextField
        fullWidth
        placeholder="Rechercher un client..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
          sx: { fontSize: '1.1rem' },
        }}
      />

      {/* Liste des clients */}
      {!isLoading && filteredClients.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <PersonOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchQuery ? 'Aucun client trouvé' : 'Aucun client enregistré'}
          </Typography>
          {!searchQuery && (
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => navigate('/clients/new')}
              sx={{ mt: 2 }}
            >
              Ajouter un client
            </Button>
          )}
        </Paper>
      ) : (
        <List sx={{ bgcolor: 'background.paper' }}>
          {filteredClients.map((client) => (
            <Paper key={client.id} sx={{ mb: 1 }}>
              <ListItem
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                }}
                onClick={() => navigate(`/clients/${client.id}`)}
              >
                <ListItemText
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {client.prenom} {client.nom}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" component="span">
                        📧 {client.email}
                      </Typography>
                      <br />
                      <Typography variant="body2" component="span">
                        📞 {client.telephone}
                      </Typography>
                      <br />
                      <Typography variant="body2" component="span" color="text.secondary">
                        📍 {client.ville}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/clients/edit/${client.id}`);
                    }}
                    sx={{ mr: 1 }}
                  >
                    <EditOutlined />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(client.id);
                    }}
                    color="error"
                  >
                    <DeleteOutlined />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Bouton flottant pour ajouter */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 20,
          background: 'linear-gradient(135deg, theme.palette.primary.main 0%, ${theme.palette.secondary.main} 100%)',
        }}
        onClick={() => navigate('/clients/new')}
      >
        <AddOutlined />
      </Fab>
    </Box>
  );
};

export default ClientsListPage;
